import { useEffect, useState } from "react";
import { getAllOrders, updateOrderStatus } from "../service/AdminService";
import { Order, OrderStatus } from "../model/types";
import { Alert } from "../util/Alert";
import { Loading } from "../util/Loading";
import { extractErrorMessage } from "../service/api";

const statuses: OrderStatus[] = [
  "PLACED",
  "PREPARING",
  "DELIVERED",
  "CANCELLED",
];

export const OrdersAdminPage = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [statusFilter, setStatusFilter] = useState<OrderStatus | "ALL">("ALL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadOrders = async () => {
    setLoading(true);
    try {
      setOrders(await getAllOrders());
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const handleStatusChange = async (orderId: number, status: OrderStatus) => {
    try {
      await updateOrderStatus(orderId, status);
      setMessage("Order status updated");
      await loadOrders();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const filteredOrders = orders.filter((order) => {
    if (statusFilter !== "ALL" && order.status !== statusFilter) {
      return false;
    }

    const createdAt = new Date(order.createdAt).getTime();

    if (dateFrom) {
      const fromTime = new Date(`${dateFrom}T00:00:00`).getTime();
      if (createdAt < fromTime) {
        return false;
      }
    }

    if (dateTo) {
      const toTime = new Date(`${dateTo}T23:59:59.999`).getTime();
      if (createdAt > toTime) {
        return false;
      }
    }

    return true;
  });

  const clearFilters = () => {
    setStatusFilter("ALL");
    setDateFrom("");
    setDateTo("");
  };

  if (loading) {
    return <Loading label="Loading orders..." />;
  }

  return (
    <section className="space-y-6">
      <div className="rounded-3xl bg-white p-6 shadow-sm">
        <p className="text-xs uppercase tracking-[0.24em] text-emerald-700">
          Admin Orders
        </p>
        <h2 className="mt-2 text-3xl font-semibold text-slate-900">
          Monitor and update all orders
        </h2>
      </div>

      {message ? (
        <Alert type="success" message={message} onClose={() => setMessage("")} />
      ) : null}
      {error ? (
        <Alert type="failed" message={error} onClose={() => setError("")} />
      ) : null}

      <div className="rounded-3xl border border-stone-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="grid gap-4 sm:grid-cols-3 lg:flex-1">
            <label className="space-y-2 text-sm font-medium text-slate-700">
              <span>Status</span>
              <select
                value={statusFilter}
                onChange={(event) =>
                  setStatusFilter(event.target.value as OrderStatus | "ALL")
                }
                className="w-full rounded-2xl border border-stone-300 px-4 py-3"
              >
                <option value="ALL">All statuses</option>
                <option value="PLACED">Placed</option>
                <option value="PREPARING">Preparing</option>
                <option value="DELIVERED">Delivered</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </label>
            <label className="space-y-2 text-sm font-medium text-slate-700">
              <span>Date from</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
                className="w-full rounded-2xl border border-stone-300 px-4 py-3"
              />
            </label>
            <label className="space-y-2 text-sm font-medium text-slate-700">
              <span>Date to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
                className="w-full rounded-2xl border border-stone-300 px-4 py-3"
              />
            </label>
          </div>

          <div className="flex items-center gap-3">
            <p className="text-sm text-stone-600">
              Showing {filteredOrders.length} of {orders.length} orders
            </p>
            <button
              type="button"
              onClick={clearFilters}
              className="rounded-2xl border border-stone-200 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-stone-50"
            >
              Clear filters
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {filteredOrders.map((order) => (
          <article
            key={order.orderId}
            className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm"
          >
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-xl font-semibold text-slate-900">
                    Order ID: {order.orderId}
                  </h3>
                  <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-stone-700">
                    {order.status}
                  </span>
                </div>
                <p className="mt-2 text-sm text-stone-600">
                  {order.customerName} • {order.deliveryAddress}
                </p>
                <p className="mt-2 text-sm text-stone-500">
                  {/* {new Date(order.createdAt).toLocaleString()} */}

                     <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-stone-700">
                    Order Created At: {new Date(order.createdAt + "Z").toLocaleString()}
                 
                  </span>   

                </p>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={order.status}
                  onChange={(event) =>
                    handleStatusChange(
                      order.orderId,
                      event.target.value as OrderStatus
                    )
                  }
                  className="rounded-2xl border border-stone-300 px-4 py-3"
                >
                  {statuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
                <div className="text-lg font-semibold text-slate-900">
                  Rs. {order.totalAmount.toFixed(2)}
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>

      {!filteredOrders.length ? (
        <div className="rounded-3xl border border-stone-200 bg-white p-6 text-sm text-stone-600 shadow-sm">
          {orders.length ? "No orders match the selected filters." : "No orders yet."}
        </div>
      ) : null}
    </section>
  );
};
